import { logger } from './logger';

interface PerformanceMetric {
  name: string;
  startTime: number;
  duration: number;
  status: 'success' | 'error';
  error?: Error;
}

class PerformanceMonitor {
  private static instance: PerformanceMonitor;
  private metrics: PerformanceMetric[] = [];
  private readonly PERFORMANCE_THRESHOLD = 3000; // 3 seconds

  private constructor() {
    this.setupPerformanceObserver();
  }

  public static getInstance(): PerformanceMonitor {
    if (!PerformanceMonitor.instance) {
      PerformanceMonitor.instance = new PerformanceMonitor();
    }
    return PerformanceMonitor.instance;
  }

  private setupPerformanceObserver() {
    if (typeof window !== 'undefined' && 'PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (entry.entryType === 'longtask' && entry.duration > this.PERFORMANCE_THRESHOLD) {
            logger.warn('Long task detected', {
              name: entry.name,
              duration: entry.duration,
              startTime: entry.startTime
            });
          }
        });
      });

      observer.observe({ entryTypes: ['longtask', 'resource', 'navigation'] });
    }
  }

  public async measure<T>(name: string, fn: () => Promise<T>): Promise<T> {
    const startTime = performance.now();
    try {
      const result = await fn();
      this.recordMetric({
        name,
        startTime,
        duration: performance.now() - startTime,
        status: 'success'
      });
      return result;
    } catch (error) {
      this.recordMetric({
        name,
        startTime,
        duration: performance.now() - startTime,
        status: 'error',
        error: error instanceof Error ? error : new Error(String(error))
      });
      throw error;
    }
  }

  private recordMetric(metric: PerformanceMetric) {
    this.metrics.push(metric);
    
    if (metric.duration > this.PERFORMANCE_THRESHOLD) {
      logger.warn(`Slow operation detected: ${metric.name}`, {
        duration: `${metric.duration.toFixed(2)}ms`,
        threshold: `${this.PERFORMANCE_THRESHOLD}ms`
      });
    }

    if (metric.status === 'error') {
      logger.error(`Operation failed: ${metric.name}`, metric.error);
    }

    // Keep only last 100 metrics to prevent memory leaks
    if (this.metrics.length > 100) {
      this.metrics = this.metrics.slice(-100);
    }
  }

  public getMetrics(filterFn?: (metric: PerformanceMetric) => boolean) {
    return filterFn ? this.metrics.filter(filterFn) : this.metrics;
  }

  public getAverageMetrics() {
    const successMetrics = this.metrics.filter(m => m.status === 'success');
    if (successMetrics.length === 0) return 0;
    
    const totalDuration = successMetrics.reduce((sum, m) => sum + m.duration, 0);
    return totalDuration / successMetrics.length;
  }
}

export const performanceMonitor = PerformanceMonitor.getInstance();

export const withPerformanceTracking = async <T>(name: string, fn: () => Promise<T>): Promise<T> => {
  return performanceMonitor.measure(name, fn);
};
